import React from 'react';
import styled from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { CurrencyIcon, TransferAmount as TransferAmountText, TransferStatusChip } from '~/lib/transfers';
import { formatTransferType, getTransferIcon } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { Flex, FormLabel, Inner, Text } from '~/styles';
import { formatDate, formatDateTime } from '~/util';

const Wrapper = styled.div`
  container-type: inline-size;
  container-name: transfer-summary;
`;

const TransferSummary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  > * {
    min-width: 0;
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: auto min-content;
    gap: 16px;
    align-items: end;
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.desktop}) {
    grid-template-columns: minmax(0, 1fr) minmax(310px, min-content);
  }
`;

const TransferAmountContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TransferMeta = styled.div`
  position: relative;
  container-type: inline-size;
  padding: 0 24px;
  margin: 0 -24px;

  &:after {
    content: '';
    opacity: 0;
    transform: scaleX(0.75);
    transition:
      opacity 0.8s,
      transform 0.8s;
  }

  @container (max-width: 570px) {
    padding: 0 12px;
    margin: 0 -12px;

    &:after {
      content: '';
      position: absolute;
      right: 24px;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 40%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 60%,
        transparent 100%
      );
      opacity: 1;
      transform: scaleX(1);
    }
  }

  ul {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    list-style: none;
    line-height: 32px;
    box-sizing: border-box;
    margin: 0;
    flex-wrap: nowrap;
    white-space: nowrap;
    width: max-content;

    width: calc(100% + 24px);
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;

    margin-left: -24px;
    padding-left: 24px;
    padding-right: 12px;

    mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);
    -webkit-mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    overflow-x: visible;
    overflow-y: visible;
    mask: none;
    -webkit-mask: none;
  }
`;

const TransferID = styled.div`
  width: 100%;
  min-width: 180px;
  box-sizing: border-box;
  height: fit-content;
  display: grid;
  grid-template-columns: min-content auto;
  align-items: center;
  align-self: flex-end;
  gap: 8px;

  border-radius: 8px;
  padding: 4px 4px 4px 12px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(200)};

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    box-shadow: none;
    margin-bottom: 0;

    display: grid;
    grid-template-columns: auto;
    border: 0;
    gap: 8px;
    padding: 0;
  }
`;

const StyledFormLabel = styled(FormLabel)`
  white-space: nowrap;
`;

const StyledCopyInput = styled(CopyInput)`
  --input-border-color: transparent;

  &:hover {
    --input-background: ${({ theme }) => theme.style.inputDefaultBackgroundColor};
    --input-border-color: ${({ theme }) => theme.style.inputDefaultHoveredBorderColor};
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    --input-border-color: inherit;
  }
`;

export const InfoForm = styled.form`
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const Date = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  svg {
    --icon-color: var(--color-muted-foreground);
  }

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const CurrencyConversion = styled(Flex)`
  svg {
    --icon-color: var(--color-muted-foreground);
  }
`;

const transferHeaderIconClassName =
  'inline-flex text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]';

const renderTransferHeaderIcon = (icon: React.ReactNode) => <span className={transferHeaderIconClassName}>{icon}</span>;

type DirectionType = {
  type: 'incoming' | 'outgoing' | 'internal';
  label?: string;
};

interface TransferPageHeaderProps {
  action?: React.ReactNode;
  amount: number | string;
  createdAt?: string;
  currencyCode?: string;
  direction: DirectionType;
  settledCurrencyAmount?: number | string;
  isAdding?: boolean;
  settledCurrencyCode?: string;
  status?: string;
  transferId?: string;
  transferType: TransferEntryType;
  className?: string;
}

export const TransferPageHeader: React.FC<React.PropsWithChildren<TransferPageHeaderProps>> = ({
  action,
  amount,
  createdAt,
  direction,
  isAdding,
  status,
  transferId,
  transferType,
  settledCurrencyAmount,
  settledCurrencyCode,
  currencyCode,
  className,
}) => {
  const renderDirectionChip = () => {
    const { type, label } = direction;

    switch (type) {
      case 'incoming':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.CircleArrowDown />)} size="large" variant="subtle">
            {label ?? 'Incoming'}
          </Chip>
        );
      case 'outgoing':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.CircleArrowUp />)} size="large" variant="subtle">
            {label ?? 'Outgoing'}
          </Chip>
        );
      case 'internal':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.Swap />)} size="large" variant="subtle">
            {label ?? 'Internal'}
          </Chip>
        );

      default:
        return null;
    }
  };
  const showCurrencyConversion = settledCurrencyAmount && settledCurrencyCode !== 'USD';

  return (
    <Inner p={24} px="var(--page-margin)" pb={16} className={className}>
      <Wrapper>
        <TransferSummary>
          <Flex flexDirection="column" gap={8}>
            <TransferAmountContainer>
              <Flex gap={8}>
                {transferType === 'swift' && renderTransferHeaderIcon(<CurrencyIcon currencyCode={currencyCode} />)}

                <TransferAmountText
                  isAdding={transferType === 'book' ? undefined : isAdding}
                  className="text-2xl font-semibold leading-6 tracking-[-0.02em]"
                  signPlacement="inline"
                >
                  {formatNumber(amount, currencyCode)}

                  <Text className="ml-1" size={14} color="lighter" weight="medium">
                    {currencyCode || 'USD'}
                  </Text>
                </TransferAmountText>
              </Flex>

              {showCurrencyConversion && (
                <CurrencyConversion gap={8} alignItems="center">
                  <Icon.ArrowRight />

                  <CurrencyIcon currencyCode={settledCurrencyCode} />

                  <Flex alignItems="baseline" gap={4}>
                    <Text size={24} weight="semibold" lineHeight="24px" letterSpacing="tightest" color="foreground">
                      {formatNumber(settledCurrencyAmount, settledCurrencyCode)}
                    </Text>
                    <Text size={14} color="lighter" weight="medium">
                      {settledCurrencyCode || 'USD'}
                    </Text>
                  </Flex>
                </CurrencyConversion>
              )}
            </TransferAmountContainer>

            <TransferMeta>
              <ul>
                <li>
                  <Chip icon={renderTransferHeaderIcon(getTransferIcon(transferType))} size="large" variant="subtle">
                    {formatTransferType(transferType, { abbreviated: false })}
                  </Chip>
                </li>
                <li>{renderDirectionChip()}</li>
                {status && (
                  <li>
                    <TransferStatusChip size="large" status={status} transferType={transferType} />
                  </li>
                )}
                {createdAt && (
                  <li>
                    <Date>
                      <DayDisplay>
                        <DayLocal>
                          {createdAt
                            ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'local' })
                                .split(' ')[1]
                                .replace(',', '')
                            : ''}
                        </DayLocal>
                        <DayUTC>
                          {createdAt
                            ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'utc' })
                                .split(' ')[1]
                                .replace(',', '')
                            : ''}
                        </DayUTC>
                        <Icon.Calendar />
                      </DayDisplay>
                      {createdAt ? (
                        <DateTimeDisplay>
                          <DateTimeLocal>
                            <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                              {formatDateTime(createdAt, { timeZoneFormat: 'local' })}
                            </Text>
                          </DateTimeLocal>
                          <DateTimeUTC>
                            <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                              {formatDateTime(createdAt, { timeZoneFormat: 'utc' })}
                            </Text>
                          </DateTimeUTC>
                        </DateTimeDisplay>
                      ) : (
                        ''
                      )}
                    </Date>
                  </li>
                )}
              </ul>
            </TransferMeta>
          </Flex>

          {transferId && (
            <TransferID>
              <StyledFormLabel>Transfer ID</StyledFormLabel>
              <StyledCopyInput value={transferId} size="small" isMonospaced />
            </TransferID>
          )}

          {action && (
            <Flex alignItems="flex-end" flexDirection="row" mb={6}>
              {action}
            </Flex>
          )}
        </TransferSummary>
      </Wrapper>
    </Inner>
  );
};
